import { useEffect, useRef } from 'react';

import { cn } from '@/utils/cn';

import { FAILED_PALETTE, type OrbIdentity } from './orbIdentity';
import {
	addOrbFrame,
	drawOrb,
	HOVER_RATE,
	ribbonDensity,
	type OrbMotion,
	type OrbPalette,
} from './orbRenderer';
import { ARRIVAL_ORB_PALETTE, type DockIconStatus } from './strategyDockData';

// Invite (empty state) → the aurora blend's LAVENDER h252, the design system's "unformed / set me
// up" eye-draw for "no strategy yet". Pinned dark enough to read clearly on the ivory/glass.
const INVITE_PALETTE: OrbPalette = { h: 252, s: 60, lFar: 26, lNear: 48 };

// Status sets how the loop MOVES; identity sets who it is. The two are kept strictly apart — if
// status touched hue, neck count or direction, a strategy going live would come back as a
// different agent.
//
// Neck depth stays well inside the envelope where the hole survives: past ~0.6 the bulges swallow
// it at any tilt and the avatar stops reading as a loop.
function motionFor(status: DockIconStatus, identity: OrbIdentity): OrbMotion {
	const base = {
		mode: 'composing' as const,
		spinDir: identity.spinDir,
		necks: identity.mode,
		undulate: 1,
	};
	switch (status.kind) {
		case 'generating':
			// Working on it: quicker, and pinching hard enough to look like it is being worked.
			return { ...base, spinRate: 1.37, neckDepth: 0.45, breathe: 0 };
		case 'failed':
			// Nearly still: a stalled strategy should look stalled.
			return { ...base, spinRate: 0.36, neckDepth: 0.26, breathe: 0 };
		case 'attention':
			// Agent needs the owner: same spin as active but with a visible slow pulse.
			return { ...base, spinRate: 0.9, neckDepth: 0.45, breathe: 1.8 };
		case 'standing':
			// Read, but still waiting on the owner's own work. Same calm breathe as active:
			// the row and the dot carry the ask, so the orb must not pulse for a week.
			return { ...base, spinRate: 0.9, neckDepth: 0.45, breathe: 0.5 };
		case 'active':
			// Running its daily loop: a steady, awake breathe so a live strategy reads as
			// working rather than merely finished.
			return { ...base, spinRate: 0.9, neckDepth: 0.45, breathe: 0.5 };
		case 'ready':
			return { ...base, spinRate: 0.79, neckDepth: 0.36, breathe: 0 };
	}
}

function reducedMotion(): boolean {
	return (
		typeof window !== 'undefined' &&
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches
	);
}

// Duration of the ghost→live palette crossfade when a slot gets filled. 360ms is the
// design system's sanctioned pop-in beat (onb-chip-pop-in), reused rather than invented.
const FILL_IN_MS = 360;

function lerp(a: number, b: number, k: number): number {
	return a + (b - a) * k;
}

// Blend two single-hue palettes. Hue interpolation is skipped when the source is fully
// desaturated (the ghost): a grey has no meaningful hue, so easing saturation and lightness
// alone reads as the colour flooding in, with no detour through an unrelated hue.
function blendPalette(from: OrbPalette, to: OrbPalette, k: number): OrbPalette {
	return {
		h: from.s === 0 ? to.h : lerp(from.h, to.h, k),
		s: lerp(from.s, to.s, k),
		lFar: lerp(from.lFar, to.lFar, k),
		lNear: lerp(from.lNear, to.lNear, k),
	};
}

// The canvas that paints a single dotted orb. Sized in CSS via --orb-size and
// painted at devicePixelRatio for crisp dots. jsdom has no 2D context, so a
// missing ctx is a no-op (unit tests just see an empty canvas).
function OrbCanvas({
	palette,
	motion,
	fillInFrom,
	interactive,
	className,
}: {
	palette: OrbPalette;
	motion: OrbMotion;
	// Live pointer state, read from a ref on every frame. Hover eases in and out rather than
	// snapping, and a press is remembered as the MOMENT it happened so the ripple can age.
	interactive?: { hovered: boolean; pressedAt: number | null };
	// When set, the orb paints this palette on its first frame and crossfades to `palette`
	// over FILL_IN_MS — the "slot comes alive" beat. The canvas keeps its box the whole
	// time, so nothing reflows and no second element swaps in.
	fillInFrom?: OrbPalette;
	className?: string;
}) {
	const ref = useRef<HTMLCanvasElement>(null);
	// Read live palette/motion from a ref so the orb subscribes to the shared
	// clock ONCE — re-subscribing on every prop change was what made the orb
	// jump/stutter at rest. State changes just update the ref, seamlessly.
	const state = useRef({ palette, motion, fillInFrom, interactive });
	state.current = { palette, motion, fillInFrom, interactive };

	useEffect(() => {
		const canvas = ref.current;
		if (!canvas) return;
		const ctx = canvas.getContext('2d');
		if (!ctx) return; // jsdom / unsupported

		const dpr = Math.min(2, window.devicePixelRatio || 1);
		let size = 0;
		const resize = () => {
			const rect = canvas.getBoundingClientRect();
			size = rect.width || parseFloat(getComputedStyle(canvas).width) || 20;
			canvas.width = Math.round(size * dpr);
			canvas.height = Math.round(size * dpr);
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		};
		resize();

		if (reducedMotion()) {
			// Static representative frame. Reduced motion lands on the FINAL state, so a
			// fill-in resolves straight to the live palette — never held at the ghost.
			drawOrb(ctx, size, 1.2, state.current.palette, state.current.motion);
			return;
		}

		const ro =
			typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
		ro?.observe(canvas);

		// Crossfade clock for the fill-in, measured off the shared rAF clock's own timestamp
		// (seconds) so it can't drift from the orb's motion or need a second timer.
		let fillStart: number | null = null;
		// The wave's phase is accumulated, never derived from the clock: hover changes the rate,
		// and recomputing phase from absolute time at that moment would jump the sash several
		// whole cycles instead of speeding it up.
		let phase = 0;
		let last: number | null = null;
		// Hover eased toward its target rather than switched, so the sash gathers and relaxes
		// instead of snapping between two animations.
		let hover = 0;
		const stop = addOrbFrame((t) => {
			const { palette: live, motion, fillInFrom, interactive } = state.current;
			const dt = last === null ? 0 : Math.min(0.05, t - last);
			last = t;

			// Hover is read from the DOM every frame rather than taken from an event, because the
			// events cannot be relied on to arrive in pairs. A leave never fires if the element is
			// hidden or detached while the pointer rests on it — nothing moved, so the browser has
			// no reason to send one — and the dock does precisely that when the menu opens. An
			// overlay appearing over the orb, a scroll under a still pointer, or a transition
			// stealing the pointer all strand it the same way.
			//
			// `:hover` alone is not enough either: it stays true on a hidden element for the same
			// reason the event never comes. So the orb needs BOTH — the browser's own hover state
			// and proof that it is still really on screen — and it re-reads them continuously, so
			// however the pointer leaves, the sash relaxes on the next frame.
			const onScreen =
				canvas.isConnected && getComputedStyle(canvas).visibility !== 'hidden';
			// The flag says the pointer entered; the DOM is asked whether it is STILL there, which
			// is what a leave event fails to tell us when the element is hidden underneath the
			// cursor. The question has to be put to the element that actually owns the hover —
			// the dock asks about its button, a card about the whole card — because the canvas
			// itself is 20px and the pointer is usually nowhere near it.
			const owner = canvas.closest('[data-orb-hover-owner]') ?? canvas;
			const pointerOn =
				(interactive?.hovered ?? false) && owner.matches(':hover');
			const target = onScreen && pointerOn ? 1 : 0;
			// Approach the target at a fixed rate per second — frame-rate independent, and it
			// settles in about the dock's 150ms transition either way.
			hover += (target - hover) * Math.min(1, dt * 9);

			const rate = motion.spinRate * (1 + (HOVER_RATE - 1) * hover);
			phase += dt * ribbonDensity(size).speed * rate;

			const pressedAt = interactive?.pressedAt ?? null;
			const pressAge = pressedAt === null ? null : t - pressedAt;
			let paint = live;
			if (fillInFrom) {
				if (fillStart === null) fillStart = t;
				const k = Math.min(1, ((t - fillStart) * 1000) / FILL_IN_MS);
				paint = k >= 1 ? live : blendPalette(fillInFrom, live, k);
			} else {
				fillStart = null;
			}
			drawOrb(ctx, size, t, paint, { ...motion, hover, pressAge }, phase);
		});
		return () => {
			stop();
			ro?.disconnect();
		};
	}, []);

	return <canvas ref={ref} aria-hidden className={cn('block', className)} />;
}

// A strategy's avatar: a living liquid loop that is the same face every time you see it.
//
// `identity` is passed IN, never derived here, and deliberately so. The dock shows the same
// strategy on several surfaces at once — the bar, a card in the menu, and an orb collapsing out of
// a row it just left — and those surfaces hold differently filtered lists. Any component that
// worked out a face from the rows it happened to be handed would give the same strategy two
// different faces on one screen, and would lose the face entirely for a row on its way out.
export function StrategyOrb({
	identity,
	status,
	fillIn = false,
	interactive,
	className,
}: {
	identity: OrbIdentity;
	status: DockIconStatus;
	// Pointer state from whatever wraps the orb. Passed in rather than bound here: the orb is a
	// bare canvas with no hit area of its own, and the button around it already owns hover and
	// press for focus, keyboard and touch.
	interactive?: { hovered: boolean; pressedAt: number | null };
	// Play the colourless→live crossfade on mount: an orb joining the dock floods with its
	// colour rather than snapping in.
	fillIn?: boolean;
	className?: string;
}) {
	const live = status.kind === 'failed' ? FAILED_PALETTE : identity.palette;
	return (
		<span className={cn('inline-block size-[var(--orb-size,20px)]', className)}>
			<OrbCanvas
				palette={live}
				fillInFrom={fillIn ? ARRIVAL_ORB_PALETTE : undefined}
				motion={motionFor(status, identity)}
				interactive={interactive}
				className="size-full"
			/>
		</span>
	);
}

// The empty-state invite orb: a shape built from tiny square pixels morphing
// circle → triangle → square → diamond → pentagon → hexagon (the "shaping"
// animation) — reads as "nothing formed yet," drawing the eye toward strategy
// setup.
export function StrategyInviteOrb({ className }: { className?: string }) {
	return (
		<span className={cn('inline-block size-[var(--orb-size,24px)]', className)}>
			<OrbCanvas
				palette={INVITE_PALETTE}
				motion={{
					mode: 'shaping',
					spinRate: 0,
					spinDir: 1,
					necks: 0,
					neckDepth: 0,
					undulate: 1,
					breathe: 1.2,
				}}
				className="size-full"
			/>
		</span>
	);
}
